import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Params, PRIMARY_OUTLET, Router } from '@angular/router';
import { GitQuery } from '@y42/git';
import { OrgQuery } from '@y42/org/state';
import { OrgSpaceQuery } from '@y42/org-space/state';
import { filter, map, Observable, pairwise, startWith } from 'rxjs';
import { Y42Route, Y42RouteDetailTab, Y42RouteDetailTabs, Y42Routes } from './y42-router.model';

@Injectable({ providedIn: 'root' })
export class Y42RouterService {
  constructor(
    private readonly router: Router,
    private readonly orgQuery: OrgQuery,
    private readonly gitQuery: GitQuery,
    private readonly orgSpaceQuery: OrgSpaceQuery,
  ) {}

  readonly navigate = this.router.navigate.bind(this.router);
  readonly navigateByUrl = this.router.navigateByUrl.bind(this.router);
  readonly parseUrl = this.router.parseUrl.bind(this.router);
  readonly events = this.router.events;

  readonly primaryOutletState$: Observable<ActivatedRouteSnapshot> = this.router.events.pipe(
    filter((e): e is NavigationEnd => e instanceof NavigationEnd),
    map(() => this.router.routerState.root.snapshot),
  );

  /**
   * Emits on URL changes on the **primary** `router-outlet`. Doesn't emit for the initial navigation.
   * Useful to listen to navigation events of only the primary `router-outlet`.
   */
  readonly primaryOutletNavigated$: Observable<unknown> = this.primaryOutletState$.pipe(
    pairwise(),
    map(([previousState, currentState]) => this.getPrimaryPath(previousState) !== this.getPrimaryPath(currentState)),
    filter(Boolean),
    map(() => true),
  );

  /**
   * Emits on URL changes on the **primary** `router-outlet` including the initial navigation.
   * Useful to listen to navigation events of only the primary `router-outlet`.
   */
  readonly primaryOutletNavigatedWithInit$: Observable<unknown> = this.primaryOutletState$.pipe(
    startWith(undefined),
    pairwise(),
    map(([previousState, currentState]) => this.getPrimaryPath(previousState) !== this.getPrimaryPath(currentState)),
    filter(Boolean),
    map(() => true),
  );

  private get activeOrgSlug() {
    return this.orgQuery.getActive()?.slug || '';
  }

  private get activeSpaceName() {
    return this.orgSpaceQuery.getActive()?.repoName || '';
  }

  private get activeBranchName() {
    return this.gitQuery.getActiveBranch()?.name || '';
  }

  get url() {
    return this.router.url;
  }

  /**
   * Returns a URL-safe string that can be used as a static identifier
   * ```
   * "/demo/space/main/visualization/123/settings" => "visualization-settings"
   * ```
   */
  get urlWithoutParams() {
    let url = this.router.url
      .replace(`${this.activeOrgSlug}`, '')
      .replace(`${this.activeSpaceName}`, '')
      .replace(`${this.activeBranchName}`, '')
      .split('/')
      .filter((segment) => !!segment)
      .join('-')
      .replace(/--/g, '-');

    let child = this.router.routerState.snapshot.root.firstChild;
    while (child) {
      const params = child ? child.params : {};
      Object.values(params).forEach((param) => (url = url.replace(param, '')));

      const nextChild = child?.firstChild;
      child = nextChild;
    }

    return url;
  }

  navigateTo(route: Y42Route, queryParams?: Params) {
    const commands = this.getURLCommandsTo(route);
    console.debug(`[RouterService]: navigating to`, commands.join('/'));
    return this.router.navigate(commands, { queryParams });
  }

  navigateToDetail(
    route: Y42Route,
    id: string,
    tabOrQueryParams?: Y42RouteDetailTab | Params,
    subId?: string,
    queryParams?: Params,
  ) {
    const isQueryParams = typeof tabOrQueryParams === 'object';
    const commands = isQueryParams
      ? this.getURLCommandsToDetail(route, id)
      : this.getURLCommandsToDetail(route, id, tabOrQueryParams, subId);
    console.debug(`[RouterService]: navigating to detail`, commands.join('/'));
    return this.router.navigate(commands, { queryParams });
  }

  getURLTo(route: Y42Route) {
    return this.router.serializeUrl(this.router.createUrlTree(this.getURLCommandsTo(route)));
  }

  getURLToDetail(route: Y42Route, id: string, tab: Y42RouteDetailTab = Y42RouteDetailTabs.Default, subId?: string) {
    return this.router.serializeUrl(this.router.createUrlTree(this.getURLCommandsToDetail(route, id, tab, subId)));
  }

  getFeatureRouteFromURL(url?: string): Y42Routes {
    url = url || this.url;
    const cleanUrl = url
      .replace(`/${this.activeOrgSlug}/`, '')
      .replace(`${this.activeSpaceName}/`, '')
      .replace(`${this.activeBranchName}/`, '');
    const allMatches = Object.values(Y42Routes).filter((route) => cleanUrl.includes(route));

    if (allMatches?.length > 1) {
      // if we have multiple matches like ['/models', '/models/sql'] -> we want to sort from the longest (the most specific) route to the shortest
      // thanks to that we gonna get specific feature route
      return (
        allMatches.sort((firstRoute, secondRoute) => secondRoute.length - firstRoute.length)[0] ?? Y42Routes.Default
      );
    }

    return allMatches?.[0] || Y42Routes.Default; // TODO: should handleError and return undefined -> not Y42Routes.Default
  }

  getURLCommandsTo(route: Y42Route): string[] {
    return [
      'org',
      this.activeOrgSlug,
      'space',
      this.activeSpaceName,
      'git',
      this.activeBranchName,
      route.split('/'),
    ].flat();
  }

  private getURLCommandsToDetail(route: Y42Route, id: string, tab?: Y42RouteDetailTab, subId?: string): string[] {
    const commands = [
      'org',
      this.activeOrgSlug,
      'space',
      this.activeSpaceName,
      'git',
      this.activeBranchName,
      route.split('/'),
      id,
    ];
    if (tab) {
      commands.push(tab);
    }
    if (subId) {
      commands.push(subId);
    }
    return commands.flat();
  }

  private getPrimaryPath(route: ActivatedRouteSnapshot | undefined): string {
    const urlSegments = [];
    while (route && (route.url.length > 0 || this.getPrimaryChild(route))) {
      urlSegments.push(...route.url);
      route = this.getPrimaryChild(route);
    }

    return urlSegments.map((s) => s.path).join('/');
  }

  private getPrimaryChild(route: ActivatedRouteSnapshot | undefined): ActivatedRouteSnapshot | undefined {
    return route?.children?.find((c) => c.outlet === PRIMARY_OUTLET);
  }
}
